import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSocialPostHistory, type SocialPostGeneration } from '@/hooks/useSocialPostHistory';
import { SocialPostCard, HashtagChip } from '@/pages/social-post/components/SocialPostCard';

const platformColors: Record<string, string> = {
  Facebook: 'bg-sky-50 text-sky-600 border-sky-100',
  Instagram: 'bg-pink-50 text-pink-600 border-pink-100',
  LinkedIn: 'bg-teal-50 text-teal-700 border-teal-100',
  X: 'bg-stone-100 text-stone-700 border-stone-200',
  TikTok: 'bg-stone-100 text-stone-700 border-stone-200',
  'Google Business Profile': 'bg-amber-50 text-amber-700 border-amber-100',
};

const platformIcons: Record<string, string> = {
  Facebook: 'ri-facebook-circle-line',
  Instagram: 'ri-instagram-line',
  LinkedIn: 'ri-linkedin-box-line',
  X: 'ri-twitter-x-line',
  TikTok: 'ri-tiktok-line',
  'Google Business Profile': 'ri-google-line',
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer shrink-0 ${
        copied
          ? 'bg-green-500 border-green-500 text-white'
          : 'bg-white border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500'
      }`}
    >
      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
    </button>
  );
}

function PostCard({ item, onDelete }: { item: SocialPostGeneration; onDelete: (id: string) => void }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLoadForEditing = useCallback(() => {
    navigate('/dashboard/social-post', {
      state: { historyItem: item },
    });
  }, [navigate, item]);

  const colorClass = platformColors[item.platform ?? ''] ?? 'bg-stone-100 text-stone-600 border-stone-200';
  const platformIcon = platformIcons[item.platform ?? ''] ?? 'ri-global-line';

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(item.id);
  };

  return (
    <div
      className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 group ${
        deleting ? 'opacity-40 pointer-events-none' : 'border-stone-100 hover:border-stone-200'
      }`}
    >
      {/* Card header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className={`w-8 h-8 flex items-center justify-center rounded-xl border ${colorClass} shrink-0`}>
              <i className={`${platformIcon} text-sm`} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-stone-900 truncate">
                {item.business_name ?? 'Unknown Business'}
              </div>
              <div className="text-xs text-stone-400">
                {formatDate(item.created_at)} · {formatTime(item.created_at)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex flex-col items-end gap-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border ${colorClass}`}>
                {item.platform ?? 'Post'}
              </span>
              {(item.post_type || item.generation_type === 'daily_content') && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-lg border bg-orange-50 text-orange-600 border-orange-100">
                  {item.generation_type === 'daily_content'
                    ? `${item.plan_quantity ?? '1'}-day plan`
                    : item.post_type}
                </span>
              )}
            </div>
            {/* Delete button */}
            {!confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-transparent text-stone-300 hover:border-red-200 hover:text-red-500 hover:bg-red-50 transition-all duration-150 cursor-pointer opacity-0 group-hover:opacity-100"
              >
                <i className="ri-delete-bin-line text-xs" />
              </button>
            )}
          </div>
        </div>

        {/* Caption preview */}
        {item.generation_type === 'daily_content' && item.daily_posts && item.daily_posts.length > 0 ? (
          <p className="text-xs text-stone-600 leading-relaxed line-clamp-2 mb-3 bg-stone-50 rounded-lg px-3 py-2 border border-stone-100">
            {item.daily_posts.length} saved post{item.daily_posts.length === 1 ? '' : 's'} · Day 1: {item.daily_posts[0]?.postType ?? 'Daily plan'}
          </p>
        ) : item.caption1 ? (
          <p className="text-xs text-stone-600 leading-relaxed line-clamp-2 mb-3 bg-stone-50 rounded-lg px-3 py-2 border border-stone-100">
            {item.caption1}
          </p>
        ) : null}

        {/* Hashtag preview */}
        {item.generation_type !== 'daily_content' && item.hashtag_suggestions && item.hashtag_suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item.hashtag_suggestions.slice(0, 4).map((tag) => (
              <span key={tag} className="text-xs text-stone-400 bg-stone-50 border border-stone-100 px-2 py-0.5 rounded-full">
                {tag}
              </span>
            ))}
            {item.hashtag_suggestions.length > 4 && (
              <span className="text-xs text-stone-400">+{item.hashtag_suggestions.length - 4} more</span>
            )}
          </div>
        )}

        {/* Delete confirm inline */}
        {confirmDelete ? (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 mb-2">
            <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
            <span className="text-xs text-red-700 font-medium flex-1">
              Delete this {item.generation_type === 'daily_content' ? 'saved plan' : 'post'}?
            </span>
            <button
              onClick={handleDelete}
              className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Delete
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={handleLoadForEditing}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 transition-all py-2 rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-magic-line" />
              Load & Edit
            </button>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-orange-500 transition-colors py-2 border border-stone-100 rounded-xl hover:border-orange-200 hover:bg-orange-50/50 cursor-pointer"
            >
              <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
              {expanded ? 'Hide details' : 'View full post'}
            </button>
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expanded && !confirmDelete && (
        <div className="border-t border-stone-100 px-4 py-4 space-y-4">
          {/* Image */}
          {item.image_url && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-image-line text-orange-400 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Generated Image</span>
              </div>
              <div className="rounded-xl overflow-hidden border border-stone-100">
                <img
                  src={item.image_url}
                  alt="Social post visual"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          {/* Daily plan details */}
          {item.generation_type === 'daily_content' && item.daily_posts && item.daily_posts.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-calendar-check-line text-emerald-500 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Saved Daily Plan</span>
              </div>
              <div className="space-y-2">
                {item.daily_posts.map((post, idx) => (
                  <div key={`${item.id}-day-${idx}`} className="bg-stone-50 rounded-xl px-3 py-3 border border-stone-100">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-stone-700">Day {idx + 1}</div>
                        <div className="text-xs text-stone-400 truncate">{post.postType}</div>
                      </div>
                      {post.cta && (
                        <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                          CTA: {post.cta}
                        </span>
                      )}
                    </div>
                    {post.hook && (
                      <p className="text-xs font-semibold text-stone-700 leading-relaxed mb-1.5">
                        {post.hook}
                      </p>
                    )}
                    <p className="text-xs text-stone-600 leading-relaxed whitespace-pre-wrap">
                      {post.caption}
                    </p>
                    {(post.hashtags?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {post.hashtags!.map((tag) => (
                          <HashtagChip key={`${item.id}-day-${idx}-${tag}`} tag={tag} />
                        ))}
                      </div>
                    )}
                    {(post.imageDirection || post.shortVideoIdea) && (
                      <div className="mt-2 grid grid-cols-1 gap-2">
                        {post.imageDirection && (
                          <div className="text-xs text-stone-500 bg-white rounded-lg border border-stone-100 px-2.5 py-2">
                            <span className="font-semibold text-stone-600">Image:</span> {post.imageDirection}
                          </div>
                        )}
                        {post.shortVideoIdea && (
                          <div className="text-xs text-stone-500 bg-white rounded-lg border border-stone-100 px-2.5 py-2">
                            <span className="font-semibold text-stone-600">Short video:</span> {post.shortVideoIdea}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Captions */}
          {item.generation_type !== 'daily_content' && (item.caption1 || item.caption2) && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-file-text-line text-teal-500 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Captions</span>
              </div>
              <div className="space-y-2">
                {item.caption1 && (
                  <div className="flex items-start gap-2 bg-stone-50 rounded-xl px-3 py-3 border border-stone-100">
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-stone-400 mb-1">Caption A</div>
                      <p className="text-xs text-stone-700 leading-relaxed whitespace-pre-wrap">{item.caption1}</p>
                    </div>
                    <CopyButton text={item.caption1} />
                  </div>
                )}
                {item.caption2 && (
                  <div className="flex items-start gap-2 bg-stone-50 rounded-xl px-3 py-3 border border-stone-100">
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-stone-400 mb-1">Caption B</div>
                      <p className="text-xs text-stone-700 leading-relaxed whitespace-pre-wrap">{item.caption2}</p>
                    </div>
                    <CopyButton text={item.caption2} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Hooks */}
          {item.generation_type !== 'daily_content' && item.hooks && item.hooks.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-fire-line text-orange-500 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Hooks</span>
              </div>
              <div className="space-y-1.5">
                {item.hooks.map((hook, idx) => (
                  <SocialPostCard
                    key={idx}
                    label="Hook"
                    content={hook}
                    icon="ri-fire-line"
                    accentBg="bg-orange-100"
                    accentText="text-orange-500"
                    index={idx}
                  />
                ))}
              </div>
            </div>
          )}

          {/* CTAs */}
          {item.generation_type !== 'daily_content' && item.cta_suggestions && item.cta_suggestions.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-cursor-fill text-rose-500 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">CTAs</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.cta_suggestions.map((cta, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-rose-50 border border-rose-100 rounded-lg px-3 py-1.5">
                    <span className="text-xs font-semibold text-rose-700">{cta}</span>
                    <CopyButton text={cta} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hashtags */}
          {item.generation_type !== 'daily_content' && item.hashtag_suggestions && item.hashtag_suggestions.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-hashtag text-stone-400 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Hashtags</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {item.hashtag_suggestions.map((tag) => (
                  <HashtagChip key={tag} tag={tag} />
                ))}
              </div>
            </div>
          )}

          {/* Visual direction */}
          {item.generation_type !== 'daily_content' && item.visual_direction && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-palette-line text-violet-500 text-sm" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Visual Direction</span>
              </div>
              <div className="flex items-start gap-2 bg-violet-50 rounded-xl px-3 py-2.5 border border-violet-100">
                <p className="text-xs text-stone-700 leading-relaxed flex-1">{item.visual_direction}</p>
                <CopyButton text={item.visual_direction} />
              </div>
            </div>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap gap-2 pt-1">
            {item.goal && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 font-medium">
                Goal: {item.goal}
              </span>
            )}
            {item.brand_voice && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-600 font-medium capitalize">
                {item.brand_voice} voice
              </span>
            )}
            {item.caption_length && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-500 font-medium">
                {item.caption_length} caption
              </span>
            )}
            {item.target_audience && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-600 font-medium">
                {item.target_audience}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialPostHistoryList() {
  const { history, loading, error, refetch, deletePost } = useSocialPostHistory(50);

  const handleDelete = useCallback(
    async (id: string) => {
      await deletePost(id);
    },
    [deletePost]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-stone-100 animate-pulse">
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-stone-100 rounded-xl" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-stone-100 rounded w-3/4" />
                  <div className="h-3 bg-stone-100 rounded w-1/2" />
                </div>
              </div>
              <div className="h-16 bg-stone-100 rounded-lg" />
              <div className="h-8 bg-stone-100 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
        <i className="ri-error-warning-line shrink-0" />
        <span className="flex-1">Could not load social post history: {error}</span>
        <button onClick={() => refetch()} className="text-xs font-bold underline cursor-pointer whitespace-nowrap">
          Retry
        </button>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-stone-100 mb-5">
          <i className="ri-instagram-line text-stone-400 text-4xl" />
        </div>
        <h2 className="text-lg font-bold text-stone-800 mb-2">No social content yet</h2>
        <p className="text-sm text-stone-500 max-w-sm leading-relaxed mb-6">
          Generate your first social post or daily plan and it&apos;ll be automatically saved here.
        </p>
        <Link
          to="/dashboard/social-post"
          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-400 text-white text-sm font-bold px-5 py-3 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all whitespace-nowrap"
        >
          <i className="ri-instagram-line" /> Create Your First Post
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {history.map((item) => (
        <PostCard key={item.id} item={item} onDelete={handleDelete} />
      ))}
    </div>
  );
}
